"use client";

import { ospThemes } from "@ospweb/sdk-ui";
import { OspModalType } from "@ospweb/sdk-ui/src/modal/types";
import { useMemo } from "react";
import { EdgenModalCreateAble } from "../EdgenModal";
import { EmptyState } from "../Empty";
import { EcosystemGrid } from "./EcosystemGrid";
import { ProjectGrid } from "./ProjectGrid";
import { ModalSection } from "./ModalSection";
import Translations from "@translate/components/HomeQuestionCard/EcosystemModal";
import { I18nUtil } from "@utils";
import { useEcosystemsList, useTokenTopDashboard } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export type EcosystemModalData = {
  type: "token" | "ecosystem";
  data: any;
};

export interface EcosystemModalProps {
  onClose: () => void;
  title?: string;
  onItemClick?: ({
    keyword,
    ecosystem,
    token,
  }: {
    keyword?: string;
    ecosystem?: string;
    token?: string;
  }) => void;
  hiddenProjectList?: string[];
}

export const EcosystemModal = ({
  onClose,
  title,
  onItemClick,
  hiddenProjectList,
}: EcosystemModalProps) => {
  const handleItemClick = (data: { keyword?: string; ecosystem?: string }) => {
    onItemClick?.(data);
    onClose();
  };

  // 获取生态系统排行榜数据
  const { data: ecosystemsData, isLoading: ecosystemsLoading } =
    useEcosystemsList({
      limit: 12,
      type: "TVL",
    });

  // 获取 Trending Projects 数据
  const { data: trendingProjects, loading: hotXRequestAgentLoading } =
    useTokenTopDashboard({
      enabled: true,
      pageSize: 100,
    });

  const trendingProjectsDataSet = useMemo(() => {
    return (
      (trendingProjects || [])
        .filter((item) => item.token_symbol)

        // @ts-ignore
        .filter((i) => i.contract_address?.[0]?.id)
        .slice(0, 6)
    );
  }, [trendingProjects]);

  const isEcosystemsDataEmpty = useMemo(() => {
    return !ecosystemsData?.length && !ecosystemsLoading;
  }, [ecosystemsData, ecosystemsLoading]);

  const isTrendingProjectsDataSetEmpty = useMemo(() => {
    return !trendingProjectsDataSet?.length && !hotXRequestAgentLoading;
  }, [trendingProjectsDataSet, hotXRequestAgentLoading]);

  const showEmpty = useMemo(() => {
    return isEcosystemsDataEmpty && isTrendingProjectsDataSetEmpty;
  }, [isEcosystemsDataEmpty, isTrendingProjectsDataSetEmpty]);

  return (
    <div>
      {showEmpty ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-6 mt-4">
          {/* Top Section */}
          {!isEcosystemsDataEmpty && (
            <ModalSection title={I18n.t("Top Chain")}>
              <EcosystemGrid
                onItemClick={handleItemClick}
                data={ecosystemsData}
                loading={ecosystemsLoading}
              />
            </ModalSection>
          )}
          {/* Hot Section */}
          {!hiddenProjectList?.includes(title || "") &&
            !isTrendingProjectsDataSetEmpty && (
              <ModalSection title={I18n.t("Hot Projects")}>
                <ProjectGrid
                  onItemClick={handleItemClick}
                  data={trendingProjectsDataSet}
                  loading={hotXRequestAgentLoading}
                />
              </ModalSection>
            )}
        </div>
      )}
    </div>
  );
};

// 定义新的可创建类型
type EcosystemModalCreateAbleProps = {
  title: string;
  hiddenProjectList: string[];
  modalProps?: OspModalType;
};

// 改造为使用 EdgenModalCreateAble 的异步函数形式
export const EcosystemModalCreateAble = async (
  props: EcosystemModalCreateAbleProps,
) => {
  const { modalProps, ...otherProps } = props;

  return new Promise<
    { keyword?: string; ecosystem?: string; token?: string } | undefined
  >((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <EcosystemModal
          {...otherProps}
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve(undefined);
          }}
          onItemClick={(item) => {
            EdgenModalCreateAble.end(false);
            resolve(item);
          }}
        />
      ),
      containerClassName: "max-w-full !md:w-[800px]",
      contentStyle: {
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 24,
        border: "1px solid rgba(255, 255, 255, 0.20)",
      },
      closeStyle: {
        backgroundColor: ospThemes.colors.background_solid_secondary,
      },
      title: props.title,
      titleClassName:
        "headline_regular_s text-content_primary bg-background_solid_secondary",
      destroyOnClose: true,
      onCancel: () => {
        EdgenModalCreateAble.end(false);
        resolve(undefined);
      },
      ...modalProps,
    });
  });
};
